import { Stack } from 'expo-router';
import rdcStrings from 'features/rdc/rdcStrings.en';
import { TFunction } from 'i18next';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import ContentList from 'shared/components/vxl/ContentList/ContentList';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import HyperlinkBaseText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkBaseText';
import { PressableAnalyticsProps } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import SuccessScreen from 'shared/patterns/vxl/SuccessScreen/SuccessScreen';
import theme from 'shared/styles/theme';

interface DepositCheckSuccessViewProps {
  amount: string;
  confirmationNumber: string;
  translatedText: TFunction<'rdc', undefined>;
  handleFundsAvailabilityAction: () => void;
  handleMobileCheckDepositTermsAction: () => void;
  handleDoneAction: () => void;
  handleDepositAnotherCheckAction: {
    onPress: () => void;
    analytics?: PressableAnalyticsProps;
  };
}

const DepositCheckSuccessView = ({
  amount,
  confirmationNumber,
  translatedText,
  handleFundsAvailabilityAction,
  handleMobileCheckDepositTermsAction,
  handleDoneAction,
  handleDepositAnotherCheckAction,
}: DepositCheckSuccessViewProps) => {
  const { outcome } = rdcStrings;

  const { top } = useSafeAreaInsets();
  useViewDidFocusScreenAnalyticsEvent({
    key: 'checkDepositInReview',
  });

  return (
    <View style={[baseStyles.container, { paddingTop: top }]} accessible>
      <Stack.Screen
        options={{
          headerShown: false,
        }}
      />
      <SuccessScreen
        visual="SIGN_A"
        titleText={translatedText('outcome.success.title', { amount: amount })}
        eyebrowText={translatedText('outcome.success.eyebrowText')}
        content={
          <View style={baseStyles.contentList}>
            <BodyBaseText style={baseStyles.content} numberOfLines={0}>
              {translatedText('outcome.success.confirmationNumber', {
                confirmationNumber: confirmationNumber,
              })}
            </BodyBaseText>
            <ContentList
              contentList={[
                <BodyBaseText key="bullet-1" numberOfLines={0}>
                  {translatedText('bullets.one', outcome.success.bullets.one)}
                </BodyBaseText>,

                <BodyBaseText key="bullet-2" numberOfLines={0}>
                  {translatedText(
                    'bullets.two.a',
                    outcome.success.bullets.two.a,
                  )}
                  <HyperlinkBaseText
                    onPress={handleFundsAvailabilityAction}
                    numberOfLines={0}
                  >
                    {translatedText(
                      'bullets.two.b',
                      outcome.success.bullets.two.b,
                    )}
                  </HyperlinkBaseText>
                  {translatedText(
                    'bullets.two.c',
                    outcome.success.bullets.two.c,
                  )}
                  <HyperlinkBaseText
                    onPress={handleMobileCheckDepositTermsAction}
                    numberOfLines={0}
                  >
                    {translatedText(
                      'bullets.two.d',
                      outcome.success.bullets.two.d,
                    )}
                  </HyperlinkBaseText>
                  {translatedText(
                    'bullets.two.e',
                    outcome.success.bullets.two.e,
                  )}
                </BodyBaseText>,
              ]}
            />
          </View>
        }
        primaryButton={{
          label: translatedText('buttons.done', outcome.success.buttons.done),
          onPress: handleDoneAction,
          analyticsId: 'goToHomeClicked',
          customAttributes: { source_name: 'check_deposit_in_review' },
        }}
        secondaryButton={{
          label: translatedText(
            'buttons.depositAnother',
            outcome.success.buttons.depositAnother,
          ),
          onPress: handleDepositAnotherCheckAction.onPress,
          analyticsId: handleDepositAnotherCheckAction.analytics?.analyticsId,
          customAttributes:
            handleDepositAnotherCheckAction.analytics?.customAttributes,
        }}
      />
    </View>
  );
};

const baseStyles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  content: {
    alignSelf: 'center',
    backgroundColor: theme.color.background.primary,
  },
  contentList: {
    backgroundColor: theme.color.background.primary,
    gap: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
  },
});

export default DepositCheckSuccessView;
